import { fabric } from 'fabric';
import ControlBase from '../fabric/ControlBase';
import ConnectorDrawingBrush, {
  CONNECTOR_OFFSET,
  NOTE_OFFSET,
} from '../stylus/ConnectorDrawingBrush';
import { STANDOFF_DISTANCE } from '../utils/connectorAttachment';
import { type AttachmentPoint, ConnectorEnd } from '../types';
import { pointNearLine } from '../utils/geometry';
import Connector, { type Attachable } from '../objects/Connector';
import { type IPoint } from 'fabric/fabric-impl';
import type Shape from '../objects/Shape';
import { type StyleOverride } from '../fabric/ObjectBehaviors';
import { hasConnectorAttachmentBehaviours } from '../objects/guards';
import { ShapeConnectControlAnimator } from '../utils/controlAnimation';
import {
  findNearbyConnectableShape,
  getTargetAttachmentPoint,
} from '../utils/shapeSearch';

const TOUCH_SIZE = 50;

export interface ShapeConnectControlOptions {
  attachmentPoint: AttachmentPoint;
  x: number;
  y: number;
  offsetX: number;
  offsetY: number;
  normal: IPoint;
  oppositeAttachmentPoint: AttachmentPoint;
}

interface ShapeConnectControl extends ControlBase, ShapeConnectControlOptions {}
// Control for creating or linking connectors between shapes.
class ShapeConnectControl extends ControlBase {
  actionName = 'connect';
  private readonly animator: ShapeConnectControlAnimator;
  currentObject: Shape;
  placeholderShape: Shape;
  nearbyShape: Attachable | null = null;
  targetAttachmentPoint: AttachmentPoint | null = null;

  constructor(options: ShapeConnectControlOptions) {
    super(options);
    this.cursorStyle = 'pointer';
    this.animator = new ShapeConnectControlAnimator();
  }

  getMouseDownHandler(eventData, fabricObject) {
    return () => {
      // Only allow actions when control is fully enabled
      if (!this._controlEnabled) {
        return false;
      }
      // Ensure control is no longer animated/enabled
      this.animator.stopAnimation();

      this.currentObject = fabricObject;
      fabricObject.__corner = 0;
      const brush = new ConnectorDrawingBrush(
        fabricObject.canvas,
        undefined,
        this,
        fabricObject
      );
      fabricObject.canvas.isDrawingMode = true;
      fabricObject.canvas.freeDrawingBrush = brush;
      fabricObject.canvas._onMouseDownInDrawingMode(eventData);

      return true;
    };
  }

  private buildPlaceholderShape(fabricObject: Shape) {
    const brush = new ConnectorDrawingBrush(
      fabricObject.canvas,
      undefined,
      this,
      fabricObject
    );
    this.placeholderShape = brush.buildShape();
  }

  onMouseOver(eventData, fabricObject) {
    this.currentObject = fabricObject;

    // Check for nearby connectable shape
    const controlPos = this.calculateControlPosition(fabricObject);
    this.nearbyShape = findNearbyConnectableShape(
      fabricObject,
      controlPos,
      this.normal
    );
    if (this.nearbyShape) {
      this.targetAttachmentPoint = getTargetAttachmentPoint(
        this.nearbyShape,
        this.attachmentPoint
      );
    } else {
      // Build placeholder shape only if no nearby shape
      this.buildPlaceholderShape(fabricObject);
      this.targetAttachmentPoint = null;
    }

    this.animator.animate('enter', {
      onRequestRender: () => {
        this.currentObject?.canvas?.requestRenderAll();
      },
    });
    super.onMouseOver(eventData, fabricObject);
  }

  onMouseOut(eventData, fabricObject) {
    this.currentObject = fabricObject;
    this.animator.animate('exit', {
      onRequestRender: () => {
        this.currentObject?.canvas?.requestRenderAll();
      },
      onComplete: () => {
        this.nearbyShape = null;
        this.targetAttachmentPoint = null;
      },
    });
    super.onMouseOut(eventData, fabricObject);
  }

  onMouseMove(eventData: any, fabricObject: Shape): void {
    this.currentObject = fabricObject;
    // If we moved into this control without a mouse over event (e.g., by moving
    // cursor quickly from shape to control), then start the animation.
    if (!this._controlEnabled && !this.animator.isAnimating) {
      this.animator.animate('enter', {
        onRequestRender: () => {
          this.currentObject?.canvas?.requestRenderAll();
        },
      });
    }
    const pointer = fabricObject.canvas?.getPointer(eventData.e);
    const controlPoint = this.calculateControlPosition(fabricObject);
    const zoom = fabricObject.canvas?.getZoom() || 1;

    // Use slightly larger radius than control size if user drags before ready
    const controlRadius = TOUCH_SIZE / 1.5 / zoom;
    if (!pointNearLine(pointer, controlPoint, controlPoint, controlRadius)) {
      this.animator.animate('exit', {
        onRequestRender: () => {
          this.currentObject?.canvas?.requestRenderAll();
        },
      });
    }

    // Rendering disrupts animation, so skip re-render if control is animating
    if (!this.animator.isAnimating) {
      super.onMouseMove(eventData, fabricObject);
    }
  }

  onMouseWheel(eventData: any, fabricObject: any): void {
    // Unfortunately, we can't reliably detect wheel events outside the object,
    // so always exit the control on wheel.
    this.animator.animate('exit', {
      onRequestRender: () => {
        this.currentObject?.canvas?.requestRenderAll();
      },
    });
    super.onMouseWheel(eventData, fabricObject);
  }

  drawingComplete() {
    this.currentObject.canvas.isDrawingMode = false;
    this.currentObject.canvas._isCurrentlyDrawing = false;
    this.currentObject.canvas._currentTransform = null;

    this.hovered = false;
  }

  attachStart(connector: Connector) {
    this.currentObject.attachConnector(
      connector,
      ConnectorEnd.CONNECTOR_START,
      this.attachmentPoint
    );
  }

  render(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    styleOverride: StyleOverride,
    fabricObject: Shape
  ) {
    // Render placeholder connector first so it appears behind control
    if (this._controlEnabled) {
      this.renderPlaceholders(ctx, left, top, fabricObject);
    }

    this._renderControl(ctx, left, top);

    if (this._controlEnabled) {
      this._renderEnabledState(ctx, left, top);
    }
  }

  private renderPlaceholders(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    fabricObject: Shape
  ) {
    if (this.nearbyShape && this.targetAttachmentPoint) {
      this.renderConnectorToNearbyShape(ctx, fabricObject);
    } else {
      if (this._showConnector) {
        this._renderPlaceholderConnector(ctx, left, top, fabricObject);
      }

      // Build the placeholder shape if it doesn't exist
      if (!this.placeholderShape) {
        this.buildPlaceholderShape(fabricObject);
      }
      this._renderPlaceholderShape(ctx, this.placeholderShape);
    }
  }

  _renderControl(ctx: CanvasRenderingContext2D, left: number, top: number) {
    ctx.save();
    if (this._controlEnabled) {
      ctx.fillStyle = '#0073CF';
    } else {
      ctx.fillStyle = '#5CA5E0';
    }
    ctx.strokeStyle = this._controlEnabled ? '#0073CF' : 'white';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(left, top, this.animator.currentSize / 2, 0, 2 * Math.PI, false);
    ctx.fill();
    ctx.stroke();

    ctx.restore();
  }

  _renderEnabledState(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number
  ) {
    ctx.save();
    this.animator.setMaxSize();
    const size = this.animator.currentSize;
    ctx.lineWidth = 2;
    ctx.strokeStyle = this._controlEnabled ? 'white' : '#0073CF';
    ctx.lineCap = 'round';
    const offset = 5;
    // Draw the vertical line of the plus
    ctx.beginPath();
    ctx.moveTo(left, top - size / 2 + offset);
    ctx.lineTo(left, top + size / 2 - offset);
    ctx.stroke();

    // Draw the horizontal line of the plus
    ctx.beginPath();
    ctx.moveTo(left - size / 2 + offset, top);
    ctx.lineTo(left + size / 2 - offset, top);
    ctx.stroke();
    ctx.restore();
  }

  // Find the position of this control relative to the canvas. This is used for
  // both detecting when the cursor is near the control (for animation) and for
  // the startpoint for the placeholder connectors, which are offset to not overlap
  // the control.
  private calculateControlPosition(fabricObject: fabric.Object) {
    const { degreesToRadians, rotatePoint } = fabric.util;

    const zoom = fabricObject.canvas?.getZoom() || 1;
    // Calculate the relative position of this control
    const { left, top, angle: angleDeg } = fabricObject;
    const angle = degreesToRadians(angleDeg || 0);
    const center = fabricObject.getCenterPoint();

    // Un-rotate the left/top point so it's where it would be before rotation
    let tl = new fabric.Point(left, top);
    tl = rotatePoint(tl, center, -angle);

    const width = fabricObject.getScaledWidth();
    const height = fabricObject.getScaledHeight();
    const adjustedWidth = (this.x + 0.5) * width;
    const adjustedHeight = (this.y + 0.5) * height;
    const x = tl.x + adjustedWidth + this.offsetX / zoom;
    const y = tl.y + adjustedHeight + this.offsetY / zoom;

    // Rotate back to match object
    const p = new fabric.Point(x, y);
    return rotatePoint(p, center, angle);
  }

  get _showConnector() {
    return (
      this.currentObject?.type === 'shape' &&
      !['note'].includes(this.currentObject.shapeName)
    );
  }

  // Control is only usable when animation has finished and control is at full size
  get _controlEnabled() {
    return this.animator.atMaxSize;
  }

  _renderPlaceholderConnector(
    ctx: CanvasRenderingContext2D,
    left: number,
    top: number,
    fabricObject: Shape
  ) {
    const { degreesToRadians, rotatePoint } = fabric.util;

    this.withoutViewportTransform(ctx, fabricObject.canvas, () => {
      const zoom = fabricObject.canvas?.getZoom() || 1;
      const { controller } = fabricObject;
      // Get start point
      const startPoint = this.calculateControlPosition(fabricObject);
      // Apply placeholder offset only for this vector
      const { offset, additionalOffset } =
        this._getPlaceholderShapeOffset(fabricObject);
      const offsetX = this.normal.x * offset + additionalOffset;
      const offsetY = this.normal.y * offset + additionalOffset;
      // Account for control offset, connector stand off, and placeholder shape
      // offset in endpoint calculation
      const spacingX =
        Math.abs(this.normal.x) * (this.offsetX / zoom) +
        this.normal.x * STANDOFF_DISTANCE;
      const spacingY =
        Math.abs(this.normal.y) * (this.offsetY / zoom) +
        this.normal.y * STANDOFF_DISTANCE;

      const angle = degreesToRadians(fabricObject.angle ?? 0);
      const endPoint = rotatePoint(
        new fabric.Point(
          startPoint.x + offsetX - spacingX,
          startPoint.y + offsetY - spacingY
        ),
        startPoint,
        angle
      );

      const connectorOptions = {
        controller,
        ...controller.defaultsManager.for('connector'),
        // Circular import prevents us importing ConnectorRouting.ROUTING_STRAIGHT const
        // Force straight line connector for preview. Rotated shapes cause weird routing.
        lineRouting: 0,
      };

      const connector = new Connector([startPoint, endPoint], connectorOptions);
      connector.pathOffset = { x: 0, y: 0 } as fabric.Point;

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = ctx.fillStyle = connector.stroke as string; // see DefaultsManager where this is set

      // Draw arrow head at the end of the connector
      connector._drawArrowHead(ctx, ConnectorEnd.CONNECTOR_END);
      ctx.fill();

      // Draw a dotted line for the placeholder connector
      ctx.beginPath();
      const spacing = Math.max(4, 4 / zoom);
      ctx.setLineDash([1 / zoom, spacing]);
      connector._drawLine(ctx);
      ctx.stroke();
    });
  }

  _renderPlaceholderShape(ctx: CanvasRenderingContext2D, fabricObject: Shape) {
    this.withoutViewportTransform(ctx, fabricObject.canvas, () => {
      fabricObject.transform(ctx);

      ctx.globalAlpha = 0.4;
      fabricObject.shape.render(ctx);
    });
  }

  private renderConnectorToNearbyShape(
    ctx: CanvasRenderingContext2D,
    sourceShape: Shape
  ) {
    this.withoutViewportTransform(ctx, sourceShape.canvas, () => {
      if (!this.nearbyShape || !this.targetAttachmentPoint) return;

      const zoom = sourceShape.canvas?.getZoom() || 1;
      const { controller } = sourceShape;

      const startPoint = this.calculateControlPosition(sourceShape);

      const connectorOptions = {
        controller,
        ...controller.defaultsManager.for('connector'),
        lineRouting: 0,
      };

      // Use a dummy end point and rely on ConnectorAttachmentBehavior for the
      // positioning logic once we have a connector
      const connector = new Connector(
        [startPoint, { x: 0, y: 0 }],
        connectorOptions
      );
      connector.pathOffset = { x: 0, y: 0 } as fabric.Point;

      if (!hasConnectorAttachmentBehaviours(this.nearbyShape)) {
        return;
      }

      const endPoint = this.nearbyShape.positionForConnectorAttachment(
        this.targetAttachmentPoint,
        connector,
        ConnectorEnd.CONNECTOR_END
      );

      if (!endPoint) return;

      connector.points[1].x = endPoint.x;
      connector.points[1].y = endPoint.y;

      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = ctx.fillStyle = connector.stroke as string;

      // Draw arrow head at the end of the connector
      connector._drawArrowHead(ctx, ConnectorEnd.CONNECTOR_END);
      ctx.fill();

      // Draw a dotted line for the preview connector
      ctx.beginPath();
      const spacing = Math.max(4, 4 / zoom);
      ctx.setLineDash([1 / zoom, spacing]);
      connector._drawLine(ctx);
      ctx.stroke();
    });
  }

  _getPlaceholderShapeOffset(fabricObject: Shape) {
    let offset = 0;
    let additionalOffset = 0;
    if (fabricObject.shapeName === 'note') {
      offset = NOTE_OFFSET;
    } else {
      offset = CONNECTOR_OFFSET - fabricObject.strokeWidth;
      const existingAttachments =
        this.currentObject.connectorsForAttachmentPoint(this.attachmentPoint);
      additionalOffset = 20 * existingAttachments.length;
    }

    return { offset, additionalOffset };
  }

  /**
   * Renders callback without any viewport transform applied.
   */
  private withoutViewportTransform(
    ctx: CanvasRenderingContext2D,
    canvas: fabric.Canvas,
    callback: () => void
  ) {
    ctx.save();
    const v = canvas.viewportTransform;
    if (v) {
      ctx.transform(v[0], v[1], v[2], v[3], v[4], v[5]);
    }
    callback();
    ctx.restore();
  }
}

export default ShapeConnectControl;
